import React, { FC, useEffect, useRef, useState } from 'react'
import { Kuchikomi, KuchikomiReply } from '../../model/Kuchikomi'
import { getKuchikomiDate } from '../../utils/datetime_util'
import CommentButton from '../buttons/comment_button'
import { useAuth } from '../../hooks/use_auth'
import KuchikomiReplyForm from '../kuchikomi/kuchikomi_reply_form'
import { RenderKuchikomiText } from '../kuchikomi/render_kuchikomi_text'
import { useNavigate } from 'react-router-dom'
import { checkEnableReply } from '../../utils/kuchikomi_util'
import { ThreeDotsIcon } from '../../images/icons/three_dots_icon'
import { useClickAway } from 'react-use'
import { useModal } from 'react-hooks-use-modal'
import DeleteIcon from '../../images/icons/delete_icon.png'
import WhiteModalContent from '../modal/white_modal_content'
import FlatButton from '../buttons/flat_button'
import { useSnackBar } from '../../providers/snack_bar_provider'

/**
 * クチコミの返信カード
 */
const KuchikomiReplyCard: FC<{
  kuchikomiReply: KuchikomiReply
  kuchikomi: Kuchikomi
  isSp: boolean
  sendReply: (replyMessage: string) => void
  deleteReply: (replyId: string) => Promise<void>
}> = ({ kuchikomiReply, isSp, sendReply, kuchikomi, deleteReply }) => {
  const { user } = useAuth()
  const KuchikomiRef = useRef<HTMLDivElement>(null)
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const [isLarge, setIsLarge] = useState<boolean>(false)
  const [openLarge, setOpenLarge] = useState<boolean>(false)
  const navigate = useNavigate()
  const [isCreatingReply, setIsCreatingReply] = useState<boolean>(false)
  const [isOpenContextMenu, setIsOpenContextMenu] = useState<boolean>(false)
  const { showSnackBar } = useSnackBar()
  const [DeleteModal, openDeleteModal, closeDeleteModal] = useModal('root', {
    preventScroll: false,
    focusTrapOptions: {
      clickOutsideDeactivates: true,
    },
  })

  const sendReplyAndClose = (replyMessage: string) => {
    sendReply(replyMessage)
    setIsCreatingReply(false)
  }

  useClickAway(contextMenuRef, () => {
    if (isOpenContextMenu) {
      setIsOpenContextMenu(false)
    }
  })

  const handleDeleteReply = async () => {
    try {
      await deleteReply(kuchikomiReply.id)
      showSnackBar(['返信を削除しました。'], 'success')
      closeDeleteModal()
    } catch (error) {
      showSnackBar(['返信の削除に失敗しました。'], 'error')
    }
  }

  useEffect(() => {
    if (KuchikomiRef.current && KuchikomiRef.current.clientHeight > 200) {
      setIsLarge(true)
    } else {
      setIsLarge(false)
    }
  }, [KuchikomiRef])

  return (
    <div className="mb-9">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center">
          <div
            className="w-8 h-8 rounded-full bg-center bg-cover mr-2"
            style={{ backgroundImage: `url(${kuchikomiReply.user.iconUrl})` }}
          />
          <p className="font-bold text-sm sm:text-base mr-3">
            {kuchikomiReply.user.displayName}
          </p>
          <p className="text-main-lightGray sm:text-sm text-xs">
            {getKuchikomiDate(kuchikomiReply.createdAt)}
          </p>
        </div>
        {kuchikomiReply.isOwn && (
          <div className="flex flex-col justify-center relative">
            <div
              className="cursor-pointer py-2 mr-2"
              onClick={() => setIsOpenContextMenu(!isOpenContextMenu)}
            >
              <ThreeDotsIcon />
            </div>
            {isOpenContextMenu && (
              <div
                className="absolute outline-1 top-8 right-0 z-20 shadow-md rounded-lg w-52"
                ref={contextMenuRef}
              >
                <div
                  className="flex items-center py-2 px-4 bg-white hover:bg-main-gray cursor-pointer"
                  onClick={() => {
                    openDeleteModal()
                    setIsOpenContextMenu(false)
                  }}
                >
                  <img src={DeleteIcon} alt="削除" className="h-4 mr-2" />
                  <p className="text-sm">返信を削除する</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* クチコミ */}
      <div className="mb-6 pl-10">
        <p
          className={`text-sm overflow-y-hidden ${
            isLarge && !openLarge ? 'max-h-[210px]' : ''
          }`}
          ref={KuchikomiRef}
        >
          <RenderKuchikomiText
            inputText={kuchikomiReply.comment}
            onClickHashtag={(hashtag) => {
              const removeHash = hashtag.replace('#', '')
              navigate(`/search/kuchikomi?keyword=${removeHash}`)
            }}
          />
        </p>
        {isLarge && (
          <div className="pt-3 flex items-center text-main-blue ">
            <span className="text-xs mr-1">{openLarge ? '▲' : '▼'}</span>
            {openLarge ? (
              <p
                onClick={() => setOpenLarge(false)}
                className="text-sm cursor-pointer"
              >
                閉じる
              </p>
            ) : (
              <p
                onClick={() => setOpenLarge(true)}
                className="text-sm cursor-pointer"
              >
                続きを見る
              </p>
            )}
          </div>
        )}
      </div>
      {/* ボタン */}
      <div className="flex justify-start pl-7">
        {user.isLogined() && checkEnableReply(user, kuchikomi) && (
          <div className="ml-3 flex flex-col justify-center mr-2">
            <CommentButton
              isActive={false}
              isSp={isSp}
              onClick={() => setIsCreatingReply(true)}
            />
          </div>
        )}
      </div>
      {/* クチコミの返信を作成中の時 */}
      {isCreatingReply && user.isLogined() && (
        <div className="py-4 sm:px-6">
          <KuchikomiReplyForm
            close={() => setIsCreatingReply(false)}
            sendReply={sendReplyAndClose}
            initialMessage={`@${kuchikomiReply.user.displayName}\n`}
          ></KuchikomiReplyForm>
        </div>
      )}
      {/* 削除確認モーダル */}
      <DeleteModal>
        <WhiteModalContent additionalClass="p-8">
          <p className="mb-8 text-center break-keep">
            本当にこの返信を
            <wbr />
            削除しますか？
          </p>
          <div className="flex flex-wrap justify-center items-center">
            <FlatButton
              onClick={handleDeleteReply}
              className="bg-main-blue !mx-2 text-white min-w-[150px] mb-3"
            >
              削除する
            </FlatButton>
            <FlatButton
              onClick={closeDeleteModal}
              className="bg-main-lightGray !mx-2 text-white min-w-[150px] mb-3"
            >
              キャンセル
            </FlatButton>
          </div>
        </WhiteModalContent>
      </DeleteModal>
    </div>
  )
}

export default KuchikomiReplyCard
